<!--
  PLACEHOLDER — Income Estimate (demo)

  KPI strip (projected income / certainty mix / avg price / active streams)
  computed from the hardcoded `streams` prop. The "3 PPAs · 4 forwards ·
  1 spot pool" line is a literal string in the template and must be made
  dynamic when this becomes a real feature.
-->
<template>
  <div class="income-kpis">
    <div class="income-kpi">
      <div class="eyebrow-label mb-1">
        Projected net income · {{ horizonLabel }}
      </div>
      <div class="income-kpi-value font-serif">
        <span class="currency">€</span>{{ fmt(totalValue) }}
      </div>
      <div class="small text-body-secondary mt-1">
        after Soldera fee ({{ Math.round(SOLDERA_FEE_PCT * 100) }}%) · gross €{{
          fmt(grossTotal)
        }}
      </div>
    </div>
    <div class="income-kpi">
      <div class="eyebrow-label mb-1">Certainty mix</div>
      <div class="certainty-meter d-flex flex-column gap-1 mt-1">
        <div class="certainty-meter-bar">
          <div class="fixed" :style="{ width: `${fixedPct}%` }" />
          <div class="variable" :style="{ width: `${variablePct}%` }" />
        </div>
        <div
          class="d-flex justify-content-between small text-body-secondary font-variant-tabular"
        >
          <span>
            <span class="text-primary fw-semibold">{{ fixedPct }}%</span> locked
          </span>
          <span>
            <span class="text-warning fw-semibold">{{ variablePct }}%</span>
            variable
          </span>
        </div>
      </div>
      <div class="small text-body-secondary mt-2">
        €{{ fmt(fixedValue) }} fixed · €{{ fmt(variableValue) }} estimated
      </div>
    </div>
    <div class="income-kpi">
      <div class="eyebrow-label mb-1">Avg price / MWh</div>
      <div class="income-kpi-value font-serif">
        <span class="currency">€</span>{{ avgPrice.toFixed(2) }}
      </div>
      <div class="small text-body-secondary mt-1">
        blended across {{ fmt(totalMwh) }} MWh
      </div>
    </div>
    <div class="income-kpi">
      <div class="eyebrow-label mb-1">Active streams</div>
      <div class="income-kpi-value font-serif">
        {{ streams.length }}<span class="unit"> streams</span>
      </div>
      <div class="small text-body-secondary mt-1">
        3 PPAs · 4 forwards · 1 spot pool
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
// Local SCSS owns the grid, eyebrow, big-number, and certainty-meter
// styling. Bootstrap doesn't have an equivalent 4-up bordered KPI grid
// or the hatched-fill progress meter, so this stays custom.
import {
  fmt,
  monthsForHorizon,
  SOLDERA_FEE_PCT,
  totalsForMonths,
  type HorizonValue,
  type Stream,
} from "./incomeEstimateDemoData";

const props = defineProps<{
  horizon: HorizonValue;
  streams: Stream[];
}>();

const monthCount = computed(() => monthsForHorizon(props.horizon));

const horizonLabel = computed(() => {
  if (props.horizon === "current-year") return "Rest of 2026";
  if (props.horizon === "next-12") return "Next 12 months";
  return "Next 2 years";
});

const sliceTotals = computed(() =>
  totalsForMonths(props.streams, monthCount.value)
);

const totalValue = computed(() => sliceTotals.value.total);
const fixedValue = computed(() => sliceTotals.value.fixed);
const variableValue = computed(() => sliceTotals.value.variable);

const fixedPct = computed(() =>
  totalValue.value ? Math.round((fixedValue.value / totalValue.value) * 100) : 0
);
const variablePct = computed(() => 100 - fixedPct.value);

const totalMwh = computed(() => {
  let sum = 0;
  const n = monthCount.value;
  for (const s of props.streams) {
    for (let i = 0; i < n; i++) sum += s.mwh[i];
  }
  return sum;
});

const grossTotal = computed(() => totalValue.value / (1 - SOLDERA_FEE_PCT));

const avgPrice = computed(() =>
  totalMwh.value ? grossTotal.value / totalMwh.value : 0
);
</script>

<style scoped lang="scss">
/* The 4-up bordered grid + hatched-fill certainty meter have no Bootstrap
   primitive, so the structural rules and the meter swatch stay here.
   Typography/spacing rely on .eyebrow-label, .font-serif, .small,
   .text-body-secondary, .text-primary, .text-warning, .fw-semibold. */
.income-kpis {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr;
  /* This component always renders inside .card.overflow-hidden with the
     HorizonToolbar directly below — the bottom border draws the divider
     between the two. */
  border-bottom: 1px solid var(--phoenix-border-color-translucent);
}
.income-kpi {
  padding: 18px 22px;
  border-right: 1px solid var(--phoenix-border-color-translucent);

  &:last-child {
    border-right: 0;
  }
}
.income-kpi-value {
  font-size: 30px;
  line-height: 1.05;
  font-weight: 500;
  letter-spacing: -0.015em;
  font-variant-numeric: tabular-nums;

  .currency,
  .unit {
    font-family: var(--phoenix-font-sans-serif);
    font-size: 14px;
    font-weight: 500;
    color: var(--phoenix-tertiary-color);
    letter-spacing: 0;
    margin-right: 4px;
    vertical-align: 0.18em;
  }
  .currency {
    margin-right: 2px;
  }
}

.certainty-meter-bar {
  display: flex;
  height: 6px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--phoenix-gray-200);

  .fixed {
    background: var(--phoenix-primary);
  }
  .variable {
    background: var(--phoenix-warning);
    background-image: repeating-linear-gradient(
      -45deg,
      var(--phoenix-warning),
      var(--phoenix-warning) 4px,
      rgba(255, 255, 255, 0.45) 4px,
      rgba(255, 255, 255, 0.45) 7px
    );
  }
}

.font-variant-tabular {
  font-variant-numeric: tabular-nums;
}
</style>
